import { GetProjectEstimatesQuery } from '@/gql/graphql';
import EstimateLastViewedIndicator from '@/ui/components/estimateLastViewedIndicator/EstimateLastViewedIndicator';
import EstimateStatusBadge from '@/ui/components/estimateStatusBadge/EstimateStatusBadge';
import { AnchorButton, Card } from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import { format } from 'date-fns';
import { FC } from 'react';
import { Link } from 'react-router-dom';
import BillSavingsSection from './BillSavingsSection';
import OutOfDateHeatLossWarning from './OutOfDateHeatLossWarning';

type Estimate = GetProjectEstimatesQuery['projectById']['estimates'][number];

type EstimateCardProps = {
  projectId: string;
  estimate: Estimate;
};

const EstimateCard: FC<EstimateCardProps> = (props) => {
  const { projectId, estimate } = props;

  const showHeatLossOutOfDateWarning =
    estimate.status !== 'ARCHIVED' && estimate.isHeatLossOutOfDate;

  return (
    <Card>
      <Card.Body className="space-y-4">
        <div className="flex items-center justify-between gap-3 border-b pb-3">
          <h3>{`Estimate ${estimate.reference}`}</h3>
          <div className="flex items-center gap-3">
            {estimate.archivedAt && (
              <div className="supporting-body flex flex-col items-end leading-tight">
                <span>Archived</span>
                <span className="font-medium">
                  {format(new Date(estimate.archivedAt), "d MMM ''yy")}
                </span>
              </div>
            )}
            {showHeatLossOutOfDateWarning && <OutOfDateHeatLossWarning />}
            <EstimateLastViewedIndicator lastViewedAt={estimate.lastViewedAt} />
            <EstimateStatusBadge
              status={estimate.status}
              sentAt={estimate.sentAt}
              bouncedAt={estimate.bouncedAt}
            />
            <AnchorButton
              as={Link}
              to={`/projects/${projectId}/estimates/${estimate.id}`}
              variant="white"
              size="sm"
            >
              View details
              <span className="sr-only">{estimate.reference}</span>
            </AnchorButton>
          </div>
        </div>
        <div className="flex flex-col-reverse gap-4 md:flex-row md:gap-6">
          <div className="max-w-[600px] flex-1 space-y-3">
            {estimate.grants.length === 0 ? (
              <div className="flex items-center justify-between">
                <strong>Total</strong>
                <CurrencyValue {...estimate.totalIncTax} />
              </div>
            ) : (
              <div className="grid grid-cols-2 items-end gap-y-1.5">
                <p>Total (including tax)</p>
                <p className="text-right">
                  <CurrencyValue {...estimate.totalIncTax} />
                </p>
                {estimate.grants.map((grant) => (
                  <GrantDeductionRow key={grant.id} grant={grant} />
                ))}
                <strong className="border-t pt-1.5">Total payable</strong>
                <strong className="text-right border-t pt-1.5">
                  <CurrencyValue {...estimate.totalPayable} />
                </strong>
              </div>
            )}
          </div>
          {estimate.billSavingsEstimate && (
            <div className="flex-1 border-b pb-4 md:border-b-0 md:border-l md:pb-0 md:pl-6">
              <BillSavingsSection
                billSavingsEstimate={estimate.billSavingsEstimate}
              />
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

type GrantDeductionRowProps = {
  grant: Estimate['grants'][number];
};

const GrantDeductionRow: FC<GrantDeductionRowProps> = (props) => {
  const { grant } = props;
  return (
    <>
      <p>{grant.name}</p>
      <p className="text-right text-green-600">
        −<CurrencyValue {...grant.amount} />
      </p>
    </>
  );
};

export default EstimateCard;
